import type { IconNameType } from '@wistia/vhs';
import { Box, Button, Icon, Image, mq, Paper, Text, useThemeColor } from '@wistia/vhs';
import type { CSSProperties, JSX } from 'react';
import { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { contrastRatioFromHex } from '~/components/AccessibleColorPicker/contrast-ratio';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import type { Trackable } from '~/onboarding/types';
import { matchString } from '~/utilities/matchers';
import { isNonEmptyString, isNotUndefined, isUndefined } from '~/utilities/type-guards';
import { useLogger } from '../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../hooks/useOnboardingContext/OnboardingContext';
import { BannerCloseButton } from './BannerCloseButton';
import type { Size } from './types';

type ImagePosition = 'end' | 'start';
type SharedStyleProps = { size: Size };

const StyledPaper = styled(Paper)<
  SharedStyleProps & {
    backgroundColor: string;
    backgroundGradient: string;
    imgPosition?: ImagePosition;
  }
>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  background-image: ${({ backgroundGradient }) => backgroundGradient};
  grid-template-areas: 'content';
  grid-template-columns: 1fr;
  overflow: hidden;
  position: relative;

  ${mq.smAndUp} {
    ${({ imgPosition }) =>
      imgPosition &&
      `
        display: grid;
        ${matchString(imgPosition)({
          start: () => `
            grid-template-areas: 'image content';
            grid-template-columns: 1fr 2fr;
          `,
          end: () => `
            grid-template-areas: 'content image';
            grid-template-columns: 2fr 1fr;
          `,
        })}
      `}
  }

  ${({ size, theme }) =>
    matchString(size)({
      lg: () => `border-radius: ${theme.spacing.space04}`,
      sm: () => `border-radius: ${theme.spacing.space02}`,
    })}
`;

const StyledImage = styled(Image)`
  display: none;
  grid-area: image;

  ${mq.smAndUp} {
    display: block;
  }
`;

const ContentContainer = styled(Box)<SharedStyleProps>`
  grid-area: content;

  ${({ size, theme }) =>
    matchString(size)({
      lg: () => `padding: ${theme.spacing.space05}`,
      sm: () => `padding: ${theme.spacing.space02}`,
    })}
`;

const TitleContainer = styled.div<SharedStyleProps>`
  align-items: center;
  display: flex;

  ${({ size, theme }) =>
    matchString(size)({
      lg: () => `
        margin-bottom: ${theme.spacing.space04};
        gap: ${theme.spacing.space02};
      `,
      sm: () => `
        margin-bottom: ${theme.spacing.space02};
        gap: ${theme.spacing.space01};
      `,
    })}
`;

const ButtonContainer = styled(Box)<SharedStyleProps>`
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.space02};

  ${({ size, theme }) =>
    matchString(size)({
      lg: () => `margin-top: ${theme.spacing.space04}`,
      sm: () => `margin-top: ${theme.spacing.space02}`,
    })}
`;

const getTextColorForBackground = (
  backgroundColor: string,
  darkTextColor: string,
  lightTextColor = '#ffffff',
) => {
  return contrastRatioFromHex(darkTextColor, backgroundColor) >=
    contrastRatioFromHex(lightTextColor, backgroundColor)
    ? darkTextColor
    : lightTextColor;
};

const getCtaButtonPosition = ({
  positionOverride,
  withImage,
  imgPosition,
}: {
  positionOverride?: 'end' | 'start';
  withImage: boolean;
  imgPosition: ImagePosition;
}) => {
  if (positionOverride) {
    return positionOverride;
  }

  if (withImage && imgPosition === 'start') {
    return 'end';
  }
  return 'start';
};

export type BannerProps = Trackable & {
  title: string;

  /**
   * A VHS icon displayed to the left of the title
   */
  titleIcon?: IconNameType;

  content: JSX.Element | string;

  /**
   * Size of the banner
   */
  size?: Size;

  /**
   * Text content for the CTA button
   */
  ctaLabel?: string;

  /**
   * Whether to show the CTA icon
   */
  hasCtaIcon?: boolean;

  /**
   * Whether to show the CTA button
   */
  ctaPosition?: 'end' | 'start';

  /**
   * Executed when the CTA button is clicked
   */
  onClickCta?: () => void;

  imgSrc?: string;

  /**
   * On what side of the banner to display the optional image
   */
  imgPosition?: ImagePosition;

  /**
   * Background color of the banner. Text color will change to meet the highest
   * contrast ratio with this color.
   */
  backgroundColor: string;

  /**
   * Background gradient of the banner. Text color will change to meet the highest
   * contrast ratio with this color.
   */
  backgroundGradient?: string;

  containerStyle?: CSSProperties;

  paperStyle?: CSSProperties;

  imageFit?: 'contain' | 'cover';

  /**
   * Executed when the dismiss button is clicked.
   * Pass noOp to make banner dismissable if additional side effects are not necessary.
   */
  onClickDismiss?: () => void;
};

export const BannerComponent = ({
  title,
  titleIcon,
  content,
  size = 'lg',
  ctaLabel,
  ctaPosition = 'start',
  hasCtaIcon = true,
  onClickCta,
  onClickDismiss,
  imgSrc,
  imgPosition = 'start',
  backgroundColor,
  backgroundGradient,
  containerStyle = {},
  paperStyle = {},
  imageFit = 'cover',
  snowplowParams,
  ...otherProps
}: BannerProps): JSX.Element | null => {
  const context = useContext(OnboardingContext);
  const logger = useLogger(context?.getCurrentFeatureId());
  useErrorBoundary((error: Error) => logger.error(error));

  const [active, setActive] = useState(true);
  const { logEvent, logEventOnce } = useSnowplow();
  const isSmall = size === 'sm';
  const withImage = isNotUndefined(imgSrc);

  const [grey900] = useThemeColor(['grey900']);
  const textColor = getTextColorForBackground(backgroundColor, grey900);
  const ctaButtonPosition = getCtaButtonPosition({
    positionOverride: ctaPosition,
    withImage,
    imgPosition,
  });

  useEffect(() => {
    if (active) {
      logEventOnce('bannerView', snowplowParams);
    }
  }, [active, logEventOnce, snowplowParams]);

  if (!active) {
    return null;
  }

  const ctaClick = () => {
    logEvent('bannerClick', snowplowParams);
    onClickCta?.();
  };

  const dismiss = () => {
    logEvent('bannerDismiss', snowplowParams);
    onClickDismiss?.();
    setActive(false);
  };

  return (
    <Box
      style={containerStyle}
      {...otherProps}
    >
      <StyledPaper
        backgroundColor={backgroundColor}
        backgroundGradient={backgroundGradient ?? ''}
        size={size}
        style={paperStyle}
        imgPosition={withImage ? imgPosition : undefined}
        elevation={0}
      >
        {withImage ? (
          <StyledImage
            src={imgSrc}
            alt=""
            fill={true}
            fit={imageFit}
          />
        ) : null}
        <ContentContainer
          size={size}
          direction="column"
          fill={true}
        >
          <TitleContainer size={size}>
            {isNonEmptyString(titleIcon) ? (
              <Icon
                type={titleIcon}
                size={isSmall ? 'sm' : 'md'}
                color={textColor}
              />
            ) : null}
            <Text
              variant={isSmall ? 'caption' : 'headline1'}
              bold={isSmall}
              colorOverride={textColor}
            >
              {title}
            </Text>
          </TitleContainer>
          <Text
            variant={isSmall ? 'caption' : 'body1'}
            colorOverride={textColor}
          >
            {content}
          </Text>
          <ButtonContainer
            size={size}
            justifyContent={ctaButtonPosition}
            fill={true}
          >
            {isUndefined(ctaLabel) || isUndefined(onClickCta) ? null : (
              <Button
                variant="secondary"
                label={ctaLabel}
                onClick={ctaClick}
                size={isSmall ? 'sm' : 'lg'}
                colorOverride={textColor}
                {...(hasCtaIcon && { iconName: 'arrow-right', iconPosition: 'end' })}
              />
            )}
            {isNotUndefined(onClickDismiss) ? (
              <BannerCloseButton
                size={size}
                onClick={dismiss}
                colorOverride={textColor}
                bgIsTransparent={isNonEmptyString(backgroundGradient)}
              />
            ) : null}
          </ButtonContainer>
        </ContentContainer>
      </StyledPaper>
    </Box>
  );
};

export const Banner = withErrorBoundary(BannerComponent);
